/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, ConnectedProps, DispatchProp } from 'react-redux';

import WidgetsIcon from '@mui/icons-material/WidgetsRounded';
import { Button, Tooltip } from '@mui/material';

import { hasOrganizationPermission, hasProjectPermission } from 'shared';

import OrgIcon from '../../../../assets/images/organisation_icon.svg';
import ProjectIcon from '../../../../assets/images/project_icon.svg';
import { ProjectListItem } from '../../../../components/ProjectListItem/ProjectListItem';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { StructuralPageTypes } from '../../../../constants/basicRoutes';
import { Namespaces } from '../../../../i18n';
import { RootState } from '../../../../store';
import { useControlOverlays } from '../../../../utils/useControlOverlays';
import { createCreateMenuStyles, StyledMenu, StyledMenuItem } from './CreateMenu.styles';

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  organizations: state.organizations,
  projects: state.projects,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type IProps = DispatchProp & PropsFromRedux;

const structuralPages = [
  {
    type: StructuralPageTypes.organization,
    icon: OrgIcon,
  },
  {
    type: StructuralPageTypes.project,
    icon: ProjectIcon,
  },
] as const;

const CreateMenuComponent = memo<IProps>(props => {
  const { t } = useTranslation(Namespaces.Navigation);
  const { organizations, projects, auth } = props;
  const isSuperuser = auth.user?.isSuperuser;
  const classes = createCreateMenuStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { doOpenCreateServiceOverlay, doOpenCreateOrganizationOverlay, doOpenCreateProjectOverlay } =
    useControlOverlays();

  const hasCreatePermissions = useMemo(() => {
    if (isSuperuser) return true;

    const userOrganizations = auth.user?.organizations || [];

    const canCreateProject = userOrganizations.some(org => hasOrganizationPermission(org.roles, 'create_project'));

    const canCreateCluster = userOrganizations.some(org =>
      org.projects.some(project => hasProjectPermission(org.roles, project.roles, 'create_cluster')),
    );

    return canCreateProject || canCreateCluster;
  }, [auth.user, isSuperuser]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!hasCreatePermissions) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onCreateServiceClick = () => {
    handleClose();
    doOpenCreateServiceOverlay();
  };

  const onCreateStructuralPageClick = (type: StructuralPageTypes) => {
    handleClose();
    switch (type) {
      case StructuralPageTypes.organization:
        doOpenCreateOrganizationOverlay();
        break;
      case StructuralPageTypes.project:
        doOpenCreateProjectOverlay();
        break;
    }
  };

  const isDisabledProductOption = projects.items.length === 0 && organizations.options.length === 0;

  return (
    <Tooltip
      title={!hasCreatePermissions ? t('create.no_permissions') : ''}
      disableHoverListener={hasCreatePermissions}
    >
      <div>
        <span>
          <Button
            aria-controls="customized-menu"
            aria-haspopup="true"
            variant="contained"
            size="small"
            data-cy="main-create-menu-button"
            disabled={!hasCreatePermissions}
            aria-disabled={!hasCreatePermissions}
            endIcon={
              <ArrowRoundedIcon
                className={cx({
                  [classes.arrow]: true,
                  [classes.arrowOpen]: Boolean(anchorEl),
                })}
              />
            }
            onClick={handleClick}
          >
            {t(`create.button`)}
          </Button>
        </span>

        <StyledMenu id="customized-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
          {structuralPages.map(({ type, icon: Icon }, index) =>
            type === StructuralPageTypes.organization && !isSuperuser ? null : (
              <StyledMenuItem
                key={index}
                className={classes.borderBottomInsideLink}
                onClick={() => onCreateStructuralPageClick(type)}
                disabled={type === StructuralPageTypes.project ? organizations.options.length === 0 : false}
                data-cy={`create-button-${type}`}
              >
                <ProjectListItem
                  icon={<img src={Icon} />}
                  text={t(`create.items.${type}`)}
                  description={`${t('create.button')} ${t(`create.items.${type}`).toLowerCase()}`}
                />
              </StyledMenuItem>
            ),
          )}

          <StyledMenuItem
            size="small"
            onClick={onCreateServiceClick}
            className={classes.productItem}
            disabled={isDisabledProductOption}
            data-cy={`create-button-service`}
          >
            <ProjectListItem text={t(`general:services.title`)} icon={<WidgetsIcon color="primary" />} />
          </StyledMenuItem>
        </StyledMenu>
      </div>
    </Tooltip>
  );
});

export const CreateMenu = connector(CreateMenuComponent);
